/* eslint-disable no-nested-ternary */
import { Box, Grid, InputAdornment, Typography } from '@mui/material';
import { addMonths, endOfDay, startOfDay } from 'date-fns';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import uniqid from 'uniqid';
import { Promotion } from '../../apiTypes';
import Confirmation from '../../components/Confirmation';
import EditModal from '../../components/EditModal';
import FormButtonSelect from '../../components/forms/FomsButtonSelect';
import FormDatePicker from '../../components/forms/FormDatePicker';
import FormDishesBundles from '../../components/forms/FormDishesBundles';
import FormPriceField from '../../components/forms/FormPriceField';
import FormSelect from '../../components/forms/FormSelect';
import FormSwitch from '../../components/forms/FormSwitch';
import FormTextField from '../../components/forms/FormTextField';
import { timeSlots } from '../../constants/dates';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useLocale } from '../../contexts/locale.context';
import { deleteDoc, setDoc, updateDoc } from '../../utils/api';
import { getWeekDayLabels } from '../../utils/dates';

type Props = {
  promotion: DbDocument<Promotion> | null;
  onClose: () => void;
};

const PromotionModal = ({ promotion, onClose }: Props) => {
  const { cafeSlug, cafe } = useAdmin();
  const { lang } = useLocale();
  const [confirm, setCorfirm] = useState(false);

  const form = useForm<Promotion>({
    defaultValues: {
      name: promotion?.name || '',
      description: promotion?.description || '',
      showOnMainPage: promotion ? promotion.showOnMainPage : true,
      isActive: promotion ? promotion.isActive : true,
      startTime: promotion?.startTime || Date.now(),
      endTime: promotion?.endTime || addMonths(Date.now(), 1).getTime(),
      discountType: promotion?.discountType || 'percentage',
      discountValue: promotion?.discountValue || '10',
      requiredDishes: promotion?.requiredDishes || [{ index: 0, items: [] }],
      onlyForClients: promotion ? promotion.onlyForClients : false,
      startHours: promotion?.startHours || '09:00',
      endHours: promotion?.endHours || '23:00',
      days: promotion?.days || {
        '0': true,
        '1': true,
        '2': true,
        '3': true,
        '4': true,
        '5': true,
        '6': true,
      },
    },
  });

  const { setValue } = form;
  const discountType = form.watch('discountType');

  const dayOfTheWeek = useMemo(() => getWeekDayLabels(lang), [lang]);

  const saveChanges = useCallback(
    async (values: Promotion) => {
      const newPromotion: Promotion = {
        ...values,
        startTime: startOfDay(values.startTime).getTime(),
        endTime: endOfDay(values.endTime).getTime(),
      };

      if (promotion) {
        await updateDoc<Promotion>(`cafes/${cafeSlug}/promotions/${promotion.id}`, newPromotion);
      } else {
        await setDoc<Promotion>(`cafes/${cafeSlug}/promotions/${uniqid()}`, newPromotion);
      }
      onClose();
    },
    [cafeSlug, onClose, promotion]
  );

  const remove = useCallback(async () => {
    if (promotion) {
      await deleteDoc(`cafes/${cafeSlug}/promotions/${promotion.id}`);
    }
    onClose();
  }, [cafeSlug, onClose, promotion]);

  const handleChangeDiscountType = useCallback(
    (type: string) => {
      if (type === 'percentage') {
        setValue('discountValue', '10');
      } else {
        setValue('discountValue', '1000');
      }
    },
    [setValue]
  );

  const handleChangePercent = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value);
      if (typeof val === 'number' && val >= 0 && val <= 100) {
        setValue('discountValue', val ? String(val) : '', { shouldDirty: true, shouldValidate: true });
      }
    },
    [setValue]
  );

  return (
    <EditModal<Promotion>
      title={t.promotion[lang]}
      open
      onClose={onClose}
      submitButton={t.save_changes[lang]}
      onSubmit={saveChanges}
      secondaryButton={promotion ? t.remove[lang] : undefined}
      onSecondaryButtonClick={promotion ? () => setCorfirm(true) : undefined}
      form={form}
      size="md"
    >
      <Grid container spacing={5}>
        <Grid item xs={12} md={6}>
          <FormTextField sx={{ mb: 3 }} name="name" label={t.name[lang]} rules={{ required: true }} fullWidth />
          <FormTextField sx={{ mb: 3 }} name="description" label={t.description[lang]} multiline rows={2} fullWidth />

          <Box sx={{ mb: 3, display: 'flex', gap: 3 }}>
            <FormDatePicker name="startTime" label={t.start_date[lang]} rules={{ required: true }} />
            <FormDatePicker name="endTime" label={t.end_date[lang]} rules={{ required: true }} />
          </Box>

          <Typography variant="body2" sx={{ ml: 1, color: (t) => t.palette.text.secondary }}>
            {t.days_promotion_applies[lang]}
          </Typography>
          <Box sx={{ display: 'flex', mb: 2 }}>
            {['0', '1', '2', '3', '4', '5', '6'].map((index) => (
              <FormSwitch
                key={index}
                name={`days.${index}`}
                checkbox
                label={dayOfTheWeek[Number(index)] || ''}
                labelPlacement="bottom"
              />
            ))}
          </Box>

          <Typography variant="body2" sx={{ ml: 1, mb: 1, color: (t) => t.palette.text.secondary }}>
            {t.hours_promotion_applies[lang]}
          </Typography>
          <Box sx={{ mb: 3, display: 'flex', gap: 1, alignItems: 'center' }}>
            <FormSelect name="startHours" options={timeSlots} />
            -
            <FormSelect name="endHours" options={timeSlots} />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 2 }}>
            <FormSwitch name="showOnMainPage" label={t.display_banner_in_qr_menu[lang]} />
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormButtonSelect
              name="discountType"
              label={t.discount_type[lang]}
              options={['percentage', 'fixed_discount']}
              onChange={handleChangeDiscountType}
            />
          </Box>

          <Box sx={{ mb: 3, maxWidth: '50%' }}>
            {discountType === 'percentage' && (
              <FormTextField
                name="discountValue"
                type="number"
                label={t.discount[lang]}
                required
                rules={{ required: true }}
                fullWidth
                onChange={handleChangePercent}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            )}

            {discountType === 'fixed_discount' && (
              <FormPriceField
                name="discountValue"
                fullWidth
                label={t.discount[lang]}
                required
                InputProps={{
                  endAdornment: <InputAdornment position="end">{cafe?.currency}</InputAdornment>,
                }}
              />
            )}
          </Box>

          <Typography variant="body2" sx={{ ml: 1, mb: 2, color: (t) => t.palette.text.secondary }}>
            {t.discount_applies_if_order_includes[lang]}
          </Typography>

          <FormDishesBundles name="requiredDishes" />
        </Grid>
      </Grid>

      {confirm && (
        <Confirmation
          open
          title={t.confirm_delete_promotion[lang]}
          onClose={() => setCorfirm(false)}
          onSubmit={remove}
        />
      )}
    </EditModal>
  );
};

export default PromotionModal;
